import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Heart, Clock, ArrowRight, Star, Users, BookOpen } from 'lucide-react';
import { monumentAPI } from '../services/api';
import { getPosts } from '../services/supabase';
import { useMonuments } from '../hooks/useMonuments';
import { useAuth } from '../hooks/useAuth';
import AdvancedSearch from '../components/Search/AdvancedSearch';
import LoadingSkeleton from '../components/UI/LoadingSkeleton';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, profile } = useAuth();
  const { searchMonuments, monuments, loading: monumentsLoading } = useMonuments();
  const [featuredMonuments, setFeaturedMonuments] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [popularSearches] = useState([
    'Taj Mahal', 'Great Wall of China', 'Colosseum', 'Machu Picchu', 'Petra'
  ]);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      // Fetch popular monuments using external APIs
      const monumentPromises = popularSearches.map(search => 
        searchMonuments(search, { limit: 1 })
      );
      
      const monumentResults = await Promise.allSettled(monumentPromises);
      const validMonuments = monumentResults
        .filter(result => result.status === 'fulfilled')
        .flatMap(result => result.value)
        .slice(0, 6);
      
      setFeaturedMonuments(validMonuments);

      // Fetch recent posts from Supabase
      const { data: postsData } = await getPosts({ limit: 6, sortBy: 'created_at' });
      setRecentPosts(postsData || []);
    } catch (error) {
      console.error('Failed to fetch home data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    if (query.trim()) {
      navigate(`/discover?search=${encodeURIComponent(query.trim())}`);
    }
  };

  const handlePopularSearch = (searchTerm) => {
    handleSearch(searchTerm);
  };

  const handleExploreClick = () => {
    navigate('/discover');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-100 via-primary-50 to-primary-200"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center opacity-20"></div>
        
        <motion.div 
          className="relative z-10 text-center px-4 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-text-primary mb-6">
            Discover History Through
            <span className="text-gradient block">Interactive Stories</span>
          </h1>
          <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
            Explore historical monuments from around the world through immersive scrollytelling experiences. 
            Journey through time with our curated collection of stories.
          </p>
          
          {/* Advanced Search */}
          <div className="max-w-2xl mx-auto mb-8">
            <AdvancedSearch onSearch={handleSearch} />
          </div>

          {/* Popular Searches */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <span className="text-gray-600 text-sm">Popular:</span>
            {popularSearches.map((search, index) => (
              <button
                key={index}
                onClick={() => handlePopularSearch(search)}
                className="px-3 py-1 bg-white/80 backdrop-blur-sm text-gray-700 rounded-full text-sm hover:bg-white transition-colors"
              >
                {search}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleExploreClick}
              className="btn-primary text-lg px-8 py-3 inline-flex items-center justify-center space-x-2"
            >
              <MapPin className="w-5 h-5" />
              <span>Explore Map</span>
            </button>
            <button
              onClick={handleExploreClick}
              className="btn-secondary text-lg px-8 py-3 inline-flex items-center justify-center space-x-2"
            >
              <BookOpen className="w-5 h-5" />
              <span>Browse Stories</span>
            </button>
            {isAuthenticated && (
              <Link
                to="/create-post"
                className="btn-accent text-lg px-8 py-3 inline-flex items-center justify-center space-x-2"
              >
                <Star className="w-5 h-5" />
                <span>Create Post</span>
              </Link>
            )}
          </div>
        </motion.div>
      </section>

      {/* Featured Monuments */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-serif font-bold text-text-primary mb-4">
              Featured Monuments
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Discover our most popular historical sites, loved by our community of history enthusiasts.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {loading ? (
              <LoadingSkeleton type="card" count={6} />
            ) : featuredMonuments.length > 0 ? (
              featuredMonuments.map((monument, index) => (
                <motion.div
                  key={`${monument.source}-${monument.id}`}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="group cursor-pointer"
                >
                  <Link to={`/story/${monument.id}?source=${monument.source}`}>
                    <div className="glass-card h-full">
                      <div className="relative h-48 mb-4 overflow-hidden rounded-lg">
                        {monument.thumbnail || monument.mainImage ? (
                          <img
                            src={monument.thumbnail || monument.mainImage}
                            alt={monument.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/400x200?text=No+Image'
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                            <MapPin className="w-12 h-12 text-blue-600" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-gray-900">
                          {monument.era || 'Unknown Era'}
                        </div>
                        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-gray-900">
                          {monument.source}
                        </div>
                      </div>
                      <h3 className="text-xl font-serif font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {monument.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {monument.description || monument.extract || 'No description available'}
                      </p>
                      <div className="flex items-center justify-between text-gray-600">
                        <div className="flex items-center space-x-1 text-xs">
                          <MapPin className="w-3 h-3" />
                          <span>{monument.source}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No monuments found. Try searching for historical sites!</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Community Posts */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              Community Posts
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover stories and insights shared by our community of history enthusiasts.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {loading ? (
              <LoadingSkeleton type="card" count={6} />
            ) : recentPosts.length > 0 ? (
              recentPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="group cursor-pointer"
                >
                  <Link to={`/posts/${post.id}`}>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden h-full hover:shadow-lg transition-shadow">
                      <div className="relative h-48 mb-4 overflow-hidden">
                        {post.image ? (
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                            <BookOpen className="w-12 h-12 text-purple-600" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-gray-900">
                          <Users className="w-3 h-3 inline mr-1" />
                          {post.author?.username || 'Anonymous'}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-xl font-serif font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {post.content || 'No content available'}
                        </p>
                        <div className="flex items-center justify-between text-gray-600">
                          <div className="flex items-center space-x-3 text-xs">
                            <span>{post.likes?.length || 0} likes</span>
                            <span>{post.comments?.length || 0} comments</span>
                          </div>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No community posts yet.</p>
                {isAuthenticated ? (
                  <Link
                    to="/create-post"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Create First Post
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Join Community
                  </Link>
                )}
              </div>
            )}
          </motion.div>

          {recentPosts.length > 0 && (
            <div className="text-center mt-8">
              <Link
                to="/posts"
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                View All Posts
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">
              Start Your Historical Journey
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join thousands of history enthusiasts exploring the world's most fascinating monuments and stories.
            </p>
            <button
              onClick={handleExploreClick}
              className="btn-primary text-lg px-8 py-4 inline-flex items-center space-x-2"
            >
              <Star className="w-5 h-5" />
              <span>Begin Exploration</span>
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
